import { ProblemError, type HttpClient } from '@/model';

export interface ContentLink { url: string; expiresAt: number }
type FileTarget = { path: string; excluded_id?: string };
export type ContentDisposition = 'attachment' | 'inline';
export const FILE_SHARE_LIMIT = 50 * 1024 * 1024;
export function canShareMedia(bytes?: number): boolean { return typeof bytes === 'number' && Number.isSafeInteger(bytes) && bytes >= 0 && bytes <= FILE_SHARE_LIMIT; }
export function isPayloadTooLarge(error: unknown): boolean { return error instanceof ProblemError && error.status === 413; }
export function isRateLimited(error: unknown): boolean { return error instanceof ProblemError && error.status === 429; }

/** Keep the evolving server request and response shape in this module alone. */
export async function mintContentLink(http: HttpClient, projectId: string, file: FileTarget, disposition: ContentDisposition, signal?: AbortSignal): Promise<ContentLink> {
    return mintLink(http, `/v1/projects/${projectId}/files/content-link`, 'files', {
        path: file.path, ...(file.excluded_id ? { excluded_id: file.excluded_id } : {}), disposition,
    }, signal);
}
export function mintMediaLink(http: HttpClient, mediaId: string, disposition: ContentDisposition, signal?: AbortSignal): Promise<ContentLink> {
    return mintLink(http, `/v1/media/${encodeURIComponent(mediaId)}/content-link`, 'media-links', { disposition }, signal);
}
type StorageTarget = { path: string; version?: number };
export function mintStorageLink(http: HttpClient, conversationId: string, file: StorageTarget, disposition: ContentDisposition, signal?: AbortSignal): Promise<ContentLink> {
    return mintLink(http, `/v1/conversations/${conversationId}/storage/content-link`, 'storage-links', { path: file.path, ...(file.version !== undefined ? { version: file.version } : {}), disposition }, signal);
}
async function mintLink(http: HttpClient, endpoint: string, kind: 'files' | 'media-links' | 'storage-links', body: object, signal?: AbortSignal): Promise<ContentLink> {
    signal?.throwIfAborted();
    let response: Record<string, unknown>;
    try { response = await http.post<Record<string, unknown>>(endpoint, body, { signal }); }
    catch (error) { if (isStaleContentLink(error)) throw new ProblemError({ status: (error as ProblemError).status, code: 'content_link_unavailable' }); throw error; }
    const { url: mintedPath, expires_at: expiration } = response;
    signal?.throwIfAborted();
    const expiresAt = typeof expiration === 'string' ? Date.parse(expiration) : NaN;
    if (typeof mintedPath !== 'string' || !new RegExp(`^/v1/${kind}/[A-Za-z0-9_-]{43}/content$`).test(mintedPath) || !Number.isFinite(expiresAt) || expiresAt <= Date.now())
        throw new Error('invalid_content_link');
    return { url: mintedPath, expiresAt };
}
export function isStaleContentLink(error: unknown): boolean {
    return error instanceof ProblemError && (error.status === 404 || error.status === 412);
}
function assertLive(link: ContentLink, signal?: AbortSignal) {
    signal?.throwIfAborted();
    if (link.expiresAt <= Date.now()) throw new ProblemError({ status: 404, code: 'content_link_expired' });
}
/** Native downloads cannot report HTTP errors to React. HEAD checks without reading any content. */
export async function checkContentLink(link: ContentLink, signal?: AbortSignal): Promise<void> {
    assertLive(link, signal);
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), 30000);
    try {
        const response = await fetch(link.url, { method: 'HEAD', credentials: 'omit', cache: 'no-store', redirect: 'error', referrerPolicy: 'no-referrer', signal: signal ? AbortSignal.any([signal, timeout.signal]) : timeout.signal });
        assertLive(link, signal);
        if (!response.ok) throw new ProblemError({ status: response.status, code: 'content_link_unavailable' });
    } finally { clearTimeout(timer); }
}
export async function downloadContentLink(http: HttpClient, projectId: string, file: FileTarget & { name: string }, signal?: AbortSignal): Promise<void> {
    const link = await mintContentLink(http, projectId, file, 'attachment', signal);
    await downloadLink(link, signal);
}
export async function downloadMediaLink(http: HttpClient, mediaId: string, _name: string, signal?: AbortSignal): Promise<void> {
    await downloadLink(await mintMediaLink(http, mediaId, 'attachment', signal), signal);
}
export async function downloadStorageLink(http: HttpClient, conversationId: string, file: StorageTarget & { name: string }, signal?: AbortSignal): Promise<void> {
    await downloadLink(await mintStorageLink(http, conversationId, file, 'attachment', signal), signal);
}
async function downloadLink(link: ContentLink, signal?: AbortSignal): Promise<void> {
    await checkContentLink(link, signal);
    const anchor = document.createElement('a');
    // Attachment disposition and filename come from the server.
    anchor.href = link.url; anchor.rel = 'noopener noreferrer'; anchor.referrerPolicy = 'no-referrer';
    document.body.append(anchor); anchor.click(); anchor.remove();
}
